import { AdditionalHigonAction } from '../core/higon/higon.model';
import { AdditionalMenuItem } from '../core/menu/menu.model';
import { CRUDPermissions } from '../core/models/layout.model';
import { LayoutViewModel } from '../core/viewModels/layout.viewModel';
import { NavigationLayoutViewModel } from '../core/navigation/navigation.viewModel';
import { ICoreDataEntity } from '@barthauer/abc/core/data';

export interface IArenaLayoutPluginNotificationInfo {
  get info(): IArenaLayoutPluginInfo;
  caption: string;
  filter: string;
  refreshInterval?: number;
  resetNotificationTimeout?: number;
  resetNotificationAction?: (entity: ICoreDataEntity) => Promise<void>;
}

export interface IArenaLayoutPluginInfo {
  get plugin(): IArenaLayoutPlugin;
  getAdditionalMenuItems?: () => Promise<AdditionalMenuItem[]>;
  getAdditionalHigonActions?: () => Promise<AdditionalHigonAction[]>;
  getCRUDPermissions?: () => Promise<CRUDPermissions>;
  getNotificationInfo?: () => Promise<IArenaLayoutPluginNotificationInfo>;
  notifyCurrentObject?: (entity: ICoreDataEntity) => Promise<void>;
}

export interface IArenaLayoutPlugin {
  update?: (
    navigationLayout: NavigationLayoutViewModel,
    layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>
  ) => Promise<IArenaLayoutPluginInfo>;
  getLayout(): Promise<LayoutViewModel>;
}

export abstract class ArenaLayoutPlugin implements IArenaLayoutPlugin, IArenaLayoutPluginInfo {
  public get plugin(): IArenaLayoutPlugin {
    return this;
  }

  private _navigationLayout: NavigationLayoutViewModel;
  public get navigationLayout(): NavigationLayoutViewModel {
    return this._navigationLayout;
  }

  public async update(
    navigationLayout: NavigationLayoutViewModel,
    layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>
  ): Promise<IArenaLayoutPluginInfo> {
    this._navigationLayout = navigationLayout;
    this._layoutFactory = layoutFactory;
    return this;
  }

  private _layoutFactory: (navigationLayout: NavigationLayoutViewModel) => Promise<LayoutViewModel>;
  private _layout: LayoutViewModel;

  public async getLayout(): Promise<LayoutViewModel> {
    if (!this._layout && this._layoutFactory) {
      this._layout = await this._layoutFactory(this.navigationLayout);
    }
    return this._layout;
  }

  public async getAdditionalMenuItems(): Promise<AdditionalMenuItem[]> {
    return undefined;
  }
  public async getAdditionalHigonActions(): Promise<AdditionalHigonAction[]> {
    return undefined;
  }
  public async getCRUDPermissions(): Promise<CRUDPermissions> {
    return undefined;
  }
  public async getNotificationInfo(): Promise<IArenaLayoutPluginNotificationInfo> {
    return undefined;
  }

  private _currentEntity: ICoreDataEntity;
  public get currentEntity(): ICoreDataEntity {
    return this._currentEntity;
  }
  public async notifyCurrentObject(entity: ICoreDataEntity): Promise<void> {
    const previousEntity = this._currentEntity;
    if (entity !== previousEntity) {
      this._currentEntity = entity;
      await this.notifyCurrentObjectChanged(previousEntity);
    }
  }

  protected async notifyCurrentObjectChanged(previousEntity: ICoreDataEntity): Promise<void> {}
}
